import { useContext, useMemo, useState, useEffect } from "react";
import { useLocalization } from "@fluent/react";
import { RadioButtons, Alert } from "@narmi/design_system";
import { useNotificationContext } from "cerulean";
import {
  useLoanMinimumPayment,
  useLoanPaymentEnabled,
} from "byzantine/src/dbbl/businessLogic/modules/loanPayment";
import utils from "byzantine/src/utils";
import Account from "byzantine/src/Account";
import { Funding } from "byzantine/src/types/models/v1";
import { formAdapters } from "../../../../../src";
import AccountContext from "../../../../contexts/AccountContext";
import { useInstitutionSettings } from "../../../../contexts/InstitutionSettingsContext";
import { useCurrentUser } from "../../../../contexts/CurrentUserContext";
import CardLogos from "../../payment_by_card/add_a_card/CardLogos";
import type { LoanPaymentFormType } from "../types";
import BankPaymentFields from "../fields/BankPaymentFields";
import CardPaymentFields from "../fields/CardPaymentFields";

interface PaymentDetailsSectionProps {
  form: LoanPaymentFormType;
  formMetadata: any;
  limits: object;
}

const PaymentDetailsSection = ({
  form,
  limits,
  formMetadata,
}: PaymentDetailsSectionProps) => {
  const { l10n } = useLocalization();
  const { accounts } = useContext(AccountContext) as { accounts: Account[] };
  const { isPaymentByCardEnabled } = useLoanPaymentEnabled();
  const { sendNegativeNotification } = useNotificationContext();
  const {
    loan_pay_by_card_fee: loanPayByCardFee = 0,
    dao_fund_card_network: daoFundCardNetwork = [],
  } = useInstitutionSettings();
  const { currentUser } = useCurrentUser();

  // Card management state
  const [cards, setCards] = useState<any[]>([]);

  // Fetch cards when component mounts
  useEffect(() => {
    const fetchCards = async () => {
      try {
        const fundingCards: Funding[] = await currentUser?.getFundingCards();
        if (fundingCards) {
          setCards(fundingCards);
        }
      } catch (error) {
        const errorMessage = l10n.getString(
          "loan-paybycard-fetch-error",
          {},
          "Unable to load payment cards. Please try again or contact support."
        );

        sendNegativeNotification(errorMessage);
      }
    };

    if (currentUser && isPaymentByCardEnabled) {
      fetchCards();
    }
  }, [currentUser, isPaymentByCardEnabled, l10n, sendNegativeNotification]);

  // Get minimum payment info for warnings
  const { formattedMinimumPayment, formattedDueDate } = useLoanMinimumPayment(
    form.values.toAccountId
  );

  // Check if we should show warning for principal payment
  const hasAmountDue = utils.parseValueAsFloat(formattedMinimumPayment) > 0;
  const shouldDisplayWarning =
    form.values.paymentType === "principal" && hasAmountDue;

  // Get loan details for warning
  const selectedLoan = useMemo(
    () => accounts.find((a: Account) => a.id === form.values.toAccountId),
    [accounts, form.values.toAccountId]
  );

  const isAmortizedLoan = selectedLoan?.isAmortizedLoan?.() || false;

  const warning = isAmortizedLoan
    ? l10n.getString("loan-paydown-principal-only-warning-amortized")
    : l10n.getString("loan-paydown-principal-only-warning", {
        amount: formattedMinimumPayment,
        dueDate: formattedDueDate,
      });

  const shouldShowFundingMethodSelection =
    isPaymentByCardEnabled &&
    selectedLoan?.isPayableByCard() &&
    (form.values.paymentType === "amountDue" ||
      form.values.paymentType === "custom");
  return (
    <>
      {/* Funding Method Selection (Bank vs Card) */}
      {shouldShowFundingMethodSelection && (
        <>
          <div className="alignChild--left--bottom">
            <div style={{ marginBottom: 0 }}>
              <RadioButtons
                name="fundingMethod"
                options={{
                  [l10n.getString("loan-paybycard-bank-account")]: "bank",
                  [l10n.getString("loan-paybycard-debit-card")]: "card",
                }}
                {...formAdapters.radio(form, "fundingMethod")}
              />
            </div>
            <div style={{ marginLeft: "auto" }}>
              <CardLogos fundCardNetwork={daoFundCardNetwork} />
            </div>
          </div>

          {/* Divider */}
          <div className="margin--top--l">
            <div
              className="margin--bottom--l border--top"
              style={{
                width: "calc(var(--space-l) + 100% + var(--space-l))",
                marginLeft: "calc(-1*var(--space-l))",
              }}
            />
          </div>
        </>
      )}

      {/* Principal Payment Warning */}
      {shouldDisplayWarning && (
        <div className="margin--bottom--l">
          <Alert
            isActive={shouldDisplayWarning}
            isDismissable={false}
            kind={isAmortizedLoan ? "info" : "warn"}
            icon={isAmortizedLoan ? "info" : "alert-triangle"}
          >
            <span>{warning}</span>
          </Alert>
        </div>
      )}

      {/* Bank Payment Details */}
      {(formMetadata.isPaymentDetailsBankVisible ||
        form.values.paymentType === "principal") &&
        !(
          form.values.paymentType === "custom" &&
          form.values.fundingMethod === "card"
        ) && <BankPaymentFields form={form} limits={limits} />}

      {/* Card Payment Details */}
      {(formMetadata.isPaymentDetailsCardVisible ||
        (form.values.paymentType === "custom" &&
          form.values.fundingMethod === "card")) && (
        <CardPaymentFields
          form={form}
          cards={cards}
          setCards={setCards}
          loanPayByCardFee={Number(loanPayByCardFee)}
        />
      )}
    </>
  );
};

export default PaymentDetailsSection;
